import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPagination, GridPaginationModel } from '@mui/x-data-grid';

import * as LoyaltyService from '~/services/LoyaltyService';

import {
  CUSTOM_RULE_TYPE_OPTIONS,
  type CustomRule,
  type CustomRuleSearchParams,
  type CustomRuleType,
  DEFAULT_PAGE_SIZE,
  MAX_END_DATE,
  MIN_START_DATE,
  POINTS_TYPE_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
  type PageInfo,
  type PointsType,
  type ProductType,
} from '~/types/loyaltyContract';

import CreateCustomRuleDialog from './CreateCustomRuleDialog';
import UpdateCustomRuleDialog from './UpdateCustomRuleDialog';

export default function LoyaltyCustomRulePage() {
  const { enqueueSnackbar } = useSnackbar();

  // Pagination state
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0, // MUI DataGrid uses 0-based indexing
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Sorting state
  type SortBy = 'createdAt' | 'updatedAt' | 'startDate' | 'endDate' | 'name';
  type SortOrder = 'asc' | 'desc';
  const [sortBy, setSortBy] = useState<SortBy>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Filter state
  const [filters, setFilters] = useState<CustomRuleSearchParams>({
    ids: [],
    offerIds: [],
    packageIds: [],
    regions: [],
    earnKeys: [],
    promoKeys: [],
    productTypes: [],
    types: [],
    pointsTypes: [],
  });

  // Form state
  const [customRuleIdInput, setCustomRuleIdInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [offerIdInput, setOfferIdInput] = useState('');
  const [packageIdInput, setPackageIdInput] = useState('');
  const [regionInput, setRegionInput] = useState('');
  const [earnKeyInput, setEarnKeyInput] = useState('');
  const [promoKeyInput, setPromoKeyInput] = useState('');
  const [selectedProductType, setSelectedProductType] = useState<ProductType | ''>('');
  const [selectedType, setSelectedType] = useState<CustomRuleType | ''>('');
  const [selectedPointsType, setSelectedPointsType] = useState<PointsType | ''>('');
  const [startDateFrom, setStartDateFrom] = useState('');
  const [endDateTo, setEndDateTo] = useState('');

  // Validation errors from API
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Dialog state
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<CustomRule | null>(null);

  // Query for fetching custom rules
  const { data, isLoading, error } = useQuery({
    queryKey: ['loyalty', 'custom-rules', filters, paginationModel, sortBy, sortOrder],
    queryFn: async ({ signal }) => {
      const params: CustomRuleSearchParams & { sortBy?: SortBy; sortOrder?: SortOrder } = {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        sortBy,
        sortOrder,
        ...filters,
      };
      return await LoyaltyService.searchCustomRules(params as any, { signal });
    },
    placeholderData: (previousData) => previousData,
    enabled: true,
    retry: false,
  });

  const customRules: Array<CustomRule> = data?.result?.customRules || [];
  const pageInfo: PageInfo | undefined = data?.result?.pageInfo;

  const totalCount: number = pageInfo?.pageCount ? pageInfo.pageCount * paginationModel.pageSize : 0;

  const handleSearch = (): void => {
    setValidationErrors({});
    const newFilters: CustomRuleSearchParams & { name?: string } = {
      ids: customRuleIdInput.trim() ? [customRuleIdInput.trim()] : [],
      name: nameInput.trim() || undefined,
      offerIds: offerIdInput.trim() ? [offerIdInput.trim()] : [],
      packageIds: packageIdInput.trim() ? [packageIdInput.trim()] : [],
      regions: regionInput.trim() ? [regionInput.trim()] : [],
      earnKeys: earnKeyInput.trim() ? [earnKeyInput.trim()] : [],
      promoKeys: promoKeyInput.trim() ? [promoKeyInput.trim()] : [],
      productTypes: selectedProductType ? [selectedProductType] : [],
      types: selectedType ? [selectedType] : [],
      pointsTypes: selectedPointsType ? [selectedPointsType] : [],
      // Convert dates to ISO 8601 format with AEST timezone handling
      startDateFrom: startDateFrom
        ? dayjs.tz(startDateFrom, 'Australia/Sydney').startOf('day').toISOString()
        : undefined,
      endDateTo: endDateTo ? dayjs.tz(endDateTo, 'Australia/Sydney').endOf('day').toISOString() : undefined,
    };

    setFilters(newFilters);
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const handleClearFilters = (): void => {
    setCustomRuleIdInput('');
    setNameInput('');
    setOfferIdInput('');
    setPackageIdInput('');
    setRegionInput('');
    setEarnKeyInput('');
    setPromoKeyInput('');
    setSelectedProductType('');
    setSelectedType('');
    setSelectedPointsType('');
    setStartDateFrom('');
    setEndDateTo('');
    setSortBy('createdAt');
    setSortOrder('desc');
    setFilters({
      ids: [],
      offerIds: [],
      packageIds: [],
      regions: [],
      earnKeys: [],
      promoKeys: [],
      productTypes: [],
      types: [],
      pointsTypes: [],
    });
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  };

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  }, []);

  const handleRowClick = useCallback((rule: CustomRule) => {
    setSelectedRule(rule);
    setIsUpdateDialogOpen(true);
  }, []);

  const handleUpdateDialogClose = useCallback(() => {
    setIsUpdateDialogOpen(false);
    setSelectedRule(null);
  }, []);

  // Define columns
  const columns: Array<GridColDef<CustomRule>> = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        width: 280,
        renderCell: ({ value }) => (
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={value}
          >
            {value}
          </Box>
        ),
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 200,
        flex: 1,
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 180,
      },
      {
        field: 'purpose',
        headerName: 'Purpose',
        width: 150,
      },
      {
        field: 'pointsType',
        headerName: 'Points Type',
        width: 130,
      },
      {
        field: 'productType',
        headerName: 'Product Type',
        width: 130,
      },
      {
        field: 'offerId',
        headerName: 'Offer ID',
        width: 120,
      },
      {
        field: 'packageId',
        headerName: 'Package ID',
        width: 120,
      },
      {
        field: 'earnKey',
        headerName: 'Earn Key',
        width: 150,
        renderCell: ({ value }) => (
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={value}
          >
            {value}
          </Box>
        ),
      },
      {
        field: 'promoKey',
        headerName: 'Promo Key',
        width: 120,
      },
      {
        field: 'region',
        headerName: 'Region',
        width: 100,
      },
      {
        field: 'pointsValue',
        headerName: 'Points Value',
        width: 120,
        type: 'number',
      },
      {
        field: 'pointsMultiplier',
        headerName: 'Multiplier',
        width: 100,
        type: 'number',
      },
      {
        field: 'pointsRatio',
        headerName: 'Ratio',
        width: 100,
        type: 'number',
      },
      {
        field: 'pointsCap',
        headerName: 'Cap',
        width: 100,
        type: 'number',
      },
      {
        field: 'startDate',
        headerName: 'Start Date',
        width: 120,
        valueFormatter: (value) => {
          if (!value) return '';
          return dayjs(value).format('DD/MM/YYYY');
        },
      },
      {
        field: 'endDate',
        headerName: 'End Date',
        width: 120,
        valueFormatter: (value) => {
          if (!value) return '';
          // Subtract 1 day to show the actual "last valid day" instead of the "expiry timestamp"
          return dayjs(value).subtract(1, 'day').format('DD/MM/YYYY');
        },
      },
    ],
    [],
  );

  // Handle errors in useEffect to prevent render-during-render issues
  useEffect(() => {
    if (error) {
      const apiError = error as { status?: number; errors?: Array<string>; message?: string };

      // Handle 400 validation errors
      if (apiError?.status === 400 && apiError?.errors) {
        const errors: Record<string, string> = {};

        // Parse validation errors like "earnKeys.0: Invalid"
        if (Array.isArray(apiError.errors)) {
          apiError.errors.forEach((errorMsg) => {
            if (typeof errorMsg === 'string') {
              const match = errorMsg.match(/^(\w+)(?:\.\d+)?:\s*(.+)$/);
              if (match) {
                const [, field, message] = match;
                errors[field] = message;
              }
            }
          });
        }

        setValidationErrors(errors);
        enqueueSnackbar(`Please fix validation errors: ${JSON.stringify(error, null, 2)}`, { variant: 'error' });
      } else if (error instanceof Error) {
        enqueueSnackbar(`Error loading custom rules: ${error.message}`, { variant: 'error' });
      }
    }
  }, [error, enqueueSnackbar]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Search Custom Rules</Typography>
        <Button variant="contained" color="primary" onClick={() => setIsCreateDialogOpen(true)}>
          Create New Rule
        </Button>
      </Box>

      {/* Search Form */}
      <Box component="form" onSubmit={(e) => e.preventDefault()} mt={2}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Custom Rule ID"
              value={customRuleIdInput}
              onChange={(e) => setCustomRuleIdInput(e.target.value)}
              error={!!validationErrors.ids}
              helperText={validationErrors.ids}
              fullWidth
            />
            <TextField
              label="Name Search"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              fullWidth
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Earn Key"
              value={earnKeyInput}
              onChange={(e) => setEarnKeyInput(e.target.value)}
              error={!!validationErrors.earnKeys}
              helperText={validationErrors.earnKeys}
              fullWidth
            />
            <TextField
              label="Promo Key"
              value={promoKeyInput}
              onChange={(e) => setPromoKeyInput(e.target.value)}
              error={!!validationErrors.promoKeys}
              helperText={validationErrors.promoKeys}
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Offer ID"
              value={offerIdInput}
              onChange={(e) => setOfferIdInput(e.target.value)}
              error={!!validationErrors.offerIds}
              helperText={validationErrors.offerIds}
              fullWidth
            />
            <TextField
              label="Package ID"
              value={packageIdInput}
              onChange={(e) => setPackageIdInput(e.target.value)}
              error={!!validationErrors.packageIds}
              helperText={validationErrors.packageIds}
              fullWidth
            />
            <TextField
              label="Region"
              value={regionInput}
              onChange={(e) => setRegionInput(e.target.value)}
              error={!!validationErrors.regions}
              helperText={validationErrors.regions}
              fullWidth
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <FormControl fullWidth error={!!validationErrors.productTypes}>
              <InputLabel>Product Type</InputLabel>
              <Select
                value={selectedProductType}
                label="Product Type"
                onChange={(e: SelectChangeEvent<ProductType | ''>) =>
                  setSelectedProductType(e.target.value as ProductType | '')
                }
              >
                <MenuItem value="">
                  <Typography sx={{ fontStyle: 'italic' }}>All</Typography>
                </MenuItem>
                {PRODUCT_TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.productTypes && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {validationErrors.productTypes}
                </Typography>
              )}
            </FormControl>

            <FormControl fullWidth error={!!validationErrors.types}>
              <InputLabel>Type</InputLabel>
              <Select
                value={selectedType}
                label="Type"
                onChange={(e: SelectChangeEvent<CustomRuleType | ''>) =>
                  setSelectedType(e.target.value as CustomRuleType | '')
                }
              >
                <MenuItem value="">
                  <Typography sx={{ fontStyle: 'italic' }}>All</Typography>
                </MenuItem>
                {CUSTOM_RULE_TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.types && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {validationErrors.types}
                </Typography>
              )}
            </FormControl>

            <FormControl fullWidth error={!!validationErrors.pointsTypes}>
              <InputLabel>Points Type</InputLabel>
              <Select
                value={selectedPointsType}
                label="Points Type"
                onChange={(e: SelectChangeEvent<PointsType | ''>) =>
                  setSelectedPointsType(e.target.value as PointsType | '')
                }
              >
                <MenuItem value="">
                  <Typography sx={{ fontStyle: 'italic' }}>All</Typography>
                </MenuItem>
                {POINTS_TYPE_OPTIONS.filter((type) => {
                  // Only allow BONUS_POINTS and BONUS_STATUS_CREDITS for new rules
                  return type === 'BONUS_POINTS' || type === 'BONUS_STATUS_CREDITS';
                }).map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.pointsTypes && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {validationErrors.pointsTypes}
                </Typography>
              )}
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Start Date From"
              type="date"
              value={startDateFrom}
              onChange={(e) => setStartDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: MIN_START_DATE,
                max: MAX_END_DATE,
              }}
              error={!!validationErrors.startDateFrom}
              helperText={validationErrors.startDateFrom || `Must be on or after ${MIN_START_DATE}`}
              fullWidth
            />
            <TextField
              label="End Date To"
              type="date"
              value={endDateTo}
              onChange={(e) => setEndDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: startDateFrom || MIN_START_DATE,
                max: MAX_END_DATE,
              }}
              error={!!validationErrors.endDateTo}
              helperText={validationErrors.endDateTo || `Maximum date: ${MAX_END_DATE}`}
              fullWidth
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e: SelectChangeEvent<SortBy>) => {
                  setSortBy(e.target.value as SortBy);
                }}
              >
                <MenuItem value="updatedAt">Updated At</MenuItem>
                <MenuItem value="createdAt">Created At</MenuItem>
                <MenuItem value="startDate">Start Date</MenuItem>
                <MenuItem value="endDate">End Date</MenuItem>
                <MenuItem value="name">Name</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Sort Order</InputLabel>
              <Select
                value={sortOrder}
                label="Sort Order"
                onChange={(e: SelectChangeEvent<SortOrder>) => {
                  setSortOrder(e.target.value as SortOrder);
                }}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={2} justifyContent="flex-start">
            <Button variant="contained" color="primary" onClick={handleSearch}>
              Search
            </Button>
            <Button variant="text" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Results Table */}
      <Box mt={4}>
        <DataGrid
          rows={customRules}
          columns={columns}
          loading={isLoading}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={totalCount}
          slots={{
            pagination: GridPagination,
          }}
          autoHeight
          disableRowSelectionOnClick
          disableColumnMenu
          getRowId={(row) => row.id}
          onRowClick={(params) => handleRowClick(params.row)}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            },
          }}
          // Prevent DataGrid from resetting page when rowCount changes
          keepNonExistentRowsSelected
        />
      </Box>

      <CreateCustomRuleDialog open={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} />
      <UpdateCustomRuleDialog open={isUpdateDialogOpen} onClose={handleUpdateDialogClose} rule={selectedRule} />
    </Box>
  );
}
